import { userSignedIn, userSignedOut } from "#redux/actions";
import { popEntryLocation, signOut } from "#redux/actions/authentication";
import { AuthenticationLayouts } from "#src/batteries-included-components/Layouts/Authentication";
import { isFeatureEnabled } from "#src/components/Redux/reducers/featureFlags";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { HomeRoute } from "#src/routes/home";
import { ExceptionUtils } from "#src/utils/exception";
import { Button } from "@validereinc/common-components";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { fetchUserLandingPageRedirect } from "./LoginCallback.helpers";

const mapDispatchToProps = {
  userSignedIn,
  userSignedOut,
  popEntryLocation,
  signOut,
};

const LoginCallback = ({
  userSignedIn,
  popEntryLocation,
  userSignedOut,
  signOut,
  location,
  history,
}: LoginCallbackProps) => {
  const [error, setError] = useState<unknown>();
  const [redirectTo, setRedirectTo] = useState<any | undefined>();

  const authContext = useAuthenticatedContext();
  const userId = authContext.claims.user?.id;
  const isAuthErrored = authContext.isErrored;

  const stripHash = () => {
    // Replacing history with the current route (/app/callback) strips the hash from the
    // URL, without redirecting or reloading the page. If the user refreshes now,
    // they'll be redirected to the login page automatically.
    history.replace(location.pathname);
  };

  useEffect(() => {
    if (!isAuthErrored) return;

    // The auth context failed (e.g. Auth0 returned an authorization error
    // like user-denied consent). Surface the error layout so the user isn't
    // stuck on the "Verifying" screen indefinitely.
    setError(new Error("Authentication failed"));
    userSignedOut();
    stripHash();
  }, [isAuthErrored]);

  useEffect(() => {
    if (!userId) return;

    // The auth code exchange (handleRedirectCallback) is handled by
    // AuthenticatedContextProvider before getTokenSilently() is called.
    // By the time userId is available the token is already in cache, so we
    // only need to mark the Redux session as logged in and resolve the
    // post-login destination here.
    userSignedIn();

    Promise.resolve()
      .then(() => {
        let entryLocation: any = popEntryLocation();

        if (!entryLocation) {
          entryLocation = { pathname: "/" };
        }

        // Only fetch landing page redirect if going to home
        if (
          entryLocation.pathname === HomeRoute.path ||
          entryLocation.pathname === "/"
        ) {
          return fetchUserLandingPageRedirect(userId).then(
            (landingPageURL) => ({
              entryLocation,
              landingPageURL,
            })
          );
        }

        // Otherwise, just return the entry location without fetching
        return { entryLocation, landingPageURL: null };
      })
      .then(({ entryLocation, landingPageURL }) => {
        let customRedirectLandingPageFound = false;

        if (
          (entryLocation.pathname === HomeRoute.path ||
            entryLocation.pathname === "/") &&
          landingPageURL
        ) {
          entryLocation = { pathname: landingPageURL };
          customRedirectLandingPageFound = true;
        }

        ExceptionUtils.registerLifecycleEvent({
          category: "app",
          type: "info",
          data: {
            to: entryLocation,
          },
          message: `Intended entry location to redirect to registered${customRedirectLandingPageFound ? `(Custom landing page redirect: ${landingPageURL})` : ""}`,
        });

        setRedirectTo(entryLocation);
      })
      .catch((err: unknown) => {
        ExceptionUtils.reportException(err, "fatal", {
          hint: "Failed to register entry location after login callback",
        });
        setError(err);
        userSignedOut();
        stripHash();
      });
  }, [userId]);

  if (!error) {
    if (redirectTo) {
      return <Redirect to={redirectTo} />;
    } else {
      return <AuthenticationLayouts.VerifyingOnLoginLayout />;
    }
  }

  return (
    <AuthenticationLayouts.ErrorOnLoginCallbackLayout
      action={
        <Button
          variant="primary"
          onClick={() => signOut()}
          icon="arrow-left"
          iconProps={{
            size: 16,
          }}
        >
          Return to Login
        </Button>
      }
    />
  );
};

type LoginCallbackProps = {
  userSignedIn: typeof userSignedIn;
  popEntryLocation: typeof popEntryLocation;
  userSignedOut: typeof userSignedOut;
  signOut: typeof signOut;
} & RouteComponentProps;

export default connect(
  (state: any) => ({
    checkIsFeatureEnabled: isFeatureEnabled(state.featureFlags),
  }),
  mapDispatchToProps
)(LoginCallback as any);
